import React, { useEffect, useCallback, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { setLoading, login, loadUserSession } from '../store/authSlice';
import AsyncStorage from '@react-native-async-storage/async-storage';
import LoginScreen from './LoginScreen';
import ChatScreen from './ChatScreen';
import GameStyleChat from './GameStyleChat';
import TranslationPracticeScreen from './TranslationPracticeScreen';
import ProfileScreen from './ProfileScreen';
import { WalletScreen } from './WalletScreen';
import SplashScreen from './SplashScreen';
import StatusBar from './StatusBar';
import TeacherGuidance from './TeacherGuidance';
import AuthService from '../services/auth';
import { APP_CONFIG } from '../config/version';
import { supabase } from '../config/supabase';

type Screen = 'splash' | 'chat' | 'practice' | 'profile' | 'wallet';

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

const MainNavigator: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isLoggedIn, isLoading, isGuestMode } = useAppSelector((state) => state.auth);
  const [currentScreen, setCurrentScreen] = useState<Screen>('splash');
  const [messages, setMessages] = useState<Message[]>([]);
  const [interfaceStyle, setInterfaceStyle] = useState<'game' | 'traditional'>(APP_CONFIG.CHAT_INTERFACE_STYLE);

  // 调试消息变化
  const debugSetMessages = (newMessages: Message[]) => {
    console.log('🔄 MainNavigator - 接收到消息更新请求');
    console.log('MainNavigator - 设置消息:', newMessages.length, '条');
    console.log('MainNavigator - 用户消息数:', newMessages.filter(m => m.isUser).length);

    // 检查数据完整性
    console.log('🔍 MainNavigator - 检查接收数据完整性:');
    newMessages.forEach((msg, index) => {
      if (!msg.hasOwnProperty('isUser')) {
        console.log(`  ⚠️ 消息 ${index + 1} 缺少 isUser 属性: "${msg.text}"`);
      } else if (typeof msg.isUser !== 'boolean') {
        console.log(`  ⚠️ 消息 ${index + 1} isUser 类型错误 (${typeof msg.isUser}): "${msg.text}"`);
      }
    });

    // 详细日志用户消息
    const userMessages = newMessages.filter(m => m.isUser);
    console.log('MainNavigator - 用户消息详细信息:');
    userMessages.forEach((msg, index) => {
      console.log(`  ${index + 1}. "${msg.text}" (ID: ${msg.id}, isUser: ${msg.isUser})`);
    });

    // 如果没有用户消息，查找可疑消息
    if (userMessages.length === 0) {
      console.log('⚠️ MainNavigator - 没有检测到用户消息，查找可疑消息:');
      newMessages.forEach((msg, index) => {
        if (msg.text && !msg.text.includes('公主殿下') && !msg.text.includes('你好！我是') &&
            !msg.text.includes('很高兴见到你') && !msg.text.includes('练习') &&
            !msg.text.includes('听起来') && !msg.text.includes('然后呢')) {
          console.log(`  🔎 可疑 ${index + 1}: "${msg.text}" (isUser: ${msg.isUser})`);
        }
      });
    }

    setMessages(newMessages);
    console.log('MainNavigator - 状态更新完成，当前消息数:', newMessages.length);
  };
  const [showSplash, setShowSplash] = useState(true);
  const [readyForPractice, setReadyForPractice] = useState(false);

  const initializeApp = useCallback(async () => {
    try {
      console.log('=== MainNavigator: Starting app initialization ===');

      // 检查Supabase session状态
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        console.log('🔍 MainNavigator - Checking existing Supabase session:', {
          hasSession: !!session,
          hasToken: !!session?.access_token,
          userId: session?.user?.id,
          sessionError: error?.message,
          tokenPreview: session?.access_token ? session.access_token.substring(0, 20) + '...' : 'none'
        });
      } catch (error) {
        console.warn('Failed to check Supabase session:', error);
      }

      // 先尝试从本地存储恢复用户状态
      await dispatch(loadUserSession());

      // 加载界面风格偏好
      await loadInterfaceStylePreference();

    } catch (error) {
      console.warn('Failed to initialize app:', error);
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  const loadInterfaceStylePreference = async () => {
    try {
      const savedStyle = await AsyncStorage.getItem('chatInterfaceStyle');
      if (savedStyle && (savedStyle === 'game' || savedStyle === 'traditional')) {
        setInterfaceStyle(savedStyle);
        console.log('MainNavigator - 加载界面风格偏好:', savedStyle);
      }
    } catch (error) {
      console.log('MainNavigator - 加载界面风格偏好失败:', error);
    }
  };

  const handleInterfaceStyleChange = async (style: 'game' | 'traditional') => {
    console.log('MainNavigator - 界面风格变更:', style);
    setInterfaceStyle(style);
  };

  useEffect(() => {
    // 初始化应用状态
    initializeApp();
  }, [initializeApp]);

  // 如果还在加载中，显示空白页面
  if (isLoading) {
    return <View style={styles.container} />;
  }

  // 显示启动闪屏
  if (showSplash) {
    return (
      <SplashScreen
        onFinish={() => {
          setShowSplash(false);
          setCurrentScreen('chat');
        }}
      />
    );
  }

  // 根据登录状态和游客模式决定显示界面
  if (!(isLoggedIn || isGuestMode)) {
    return <LoginScreen />;
  }

  const renderMainContent = () => {
    switch (currentScreen) {
      case 'chat':
        // 根据用户偏好选择界面风格
        if (interfaceStyle === 'traditional') {
          return <ChatScreen
            key="traditional-chat"
            onNavigateToPractice={() => setCurrentScreen('practice')}
            onNavigateToProfile={() => setCurrentScreen('profile')}
            onMessagesChange={debugSetMessages}
          />;
        } else {
          return <GameStyleChat
            key="game-chat" // 添加key确保组件状态持久化
            onNavigateToPractice={() => setCurrentScreen('practice')}
            onNavigateToProfile={() => setCurrentScreen('profile')}
            onMessagesChange={debugSetMessages}
            initialMessages={messages} // 传递已有消息
          />;
        }
      case 'practice':
        console.log('MainNavigator - 渲染练习界面，传递消息数:', messages.length);
        console.log('MainNavigator - 传递给练习界面的用户消息数:', messages.filter(m => m.isUser).length);
        return <TranslationPracticeScreen
          onBackToChat={() => setCurrentScreen('chat')}
          messages={messages}
        />;
      case 'profile':
        return <ProfileScreen
          onBackToChat={() => setCurrentScreen('chat')}
          onNavigateToPractice={(exercises) => {
            setCurrentScreen('practice');
            // TODO: 传递历史练习题目给练习页面
          }}
          onInterfaceStyleChange={handleInterfaceStyleChange}
          onNavigateToWallet={() => setCurrentScreen('wallet')}
        />;
      case 'wallet':
        return <WalletScreen onBackToChat={() => setCurrentScreen('chat')} />;
      default:
        // 默认情况也根据用户偏好选择界面风格
        if (interfaceStyle === 'traditional') {
          return <ChatScreen
            key="traditional-chat-default"
            onNavigateToPractice={() => setCurrentScreen('practice')}
            onNavigateToProfile={() => setCurrentScreen('profile')}
            onMessagesChange={debugSetMessages}
          />;
        } else {
          return <GameStyleChat
            key="game-chat-default" // 添加key确保组件状态持久化
            onNavigateToPractice={() => setCurrentScreen('practice')}
            onNavigateToProfile={() => setCurrentScreen('profile')}
            onMessagesChange={debugSetMessages}
            initialMessages={messages} // 传递已有消息
          />;
        }
    }
  };

  return (
    <View style={styles.container}>
      {/* 传统界面需要StatusBar，游戏风格界面已集成导航功能 */}
      {(currentScreen !== 'chat' || interfaceStyle === 'traditional') &&
        <StatusBar onNavigateToProfile={() => setCurrentScreen('profile')} />}
      {renderMainContent()}

      {/* 全局教师指导系统（仅在非聊天界面显示，避免重复） */}
      {currentScreen !== 'chat' && currentScreen !== 'splash' && (
        <TeacherGuidance
          messageCount={messages.length}
          practiceReady={readyForPractice}
          currentScreen={currentScreen}
          onNavigateToPractice={() => setCurrentScreen('practice')}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f0f0f0',
  },
});

export default MainNavigator;
